import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faMinus, faPlus} from '@fortawesome/free-solid-svg-icons'
import {useAppDispatch} from '../../stores/configureStore'
import {setAttribute} from '../../stores/editor/editor.reducer'
import RightArrowIcon from '../icons/RightArrowIcon'
import BringForwardButton from './buttons/BringForwardButton'
import SendBackButton from './buttons/SendBackButton'
import {ImgMasked} from '../../models/editor/imgMasked'
import SelectImageButton from './SelectImageButton'
import cssCaption from '../../css/caption.module.css'
import MovementArrows from './MovementArrows'
import MirrorArrows from './MirrorArrows'
import BrightnessController from './sliders/BrightnessController'
import ImgFilters from './ImgFilters'
import RotationController from './sliders/RotationController'
import ScaleController from './sliders/ScaleController'
import ControlGroup from './ControlGroup'
import {handleUpdatedAttributes} from '../../utils/helpers/attributeHelper'
import cssMaskedPhoto from '../../css/maskedPhoto.module.css'
import {useTranslation} from 'react-i18next'

interface MaskedPhotoProps {
  img: ImgMasked
}

export default function MaskedPhoto({img}: MaskedPhotoProps) {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()

  return (
    <div className={cssMaskedPhoto.container}>
      {img.captions.map((caption, index) => (
        <div key={`${index}-key`}>
          <div
            className={[
              cssMaskedPhoto.controlWrapper,
              cssCaption.charCounterInputWrapper,
            ].join(' ')}>
            <input
              style={{marginBottom: '18px'}}
              className={cssCaption.input}
              type="text"
              value={caption.caption1}
              onChange={e => {
                const trunctatedText = e.target.value
                  .substring(0, 35)
                  .replace(/[\u0400-\uF8FF]/g, '')
                if (trunctatedText.length <= 35) {
                  let newCaptions = [...img.captions]
                  newCaptions[index] = {
                    ...newCaptions[index],
                    caption1: trunctatedText,
                  }

                  handleUpdatedAttributes(dispatch, img, {
                    captions: newCaptions,
                  })
                }
              }}
            />
            <div className={cssCaption.charCounter}>
              {35 - caption.caption1.length}/35
            </div>
          </div>
        </div>
      ))}
      <span className={'groupTitle'}>{t('Kép feltöltése')}</span>
      <div className={cssMaskedPhoto.controlGroup}>
        <div className={cssMaskedPhoto.imagesControls}>
          {img.inside.map((_, index) => (
            <SelectImageButton
              key={`modifyInsideImage_${img.id}_${index}`}
              img={img}
              index={index}
            />
          ))}
        </div>
      </div>
      <ControlGroup title={t('Sorrend')}>
        <div className={cssMaskedPhoto.imagesControls}>
          <SendBackButton />
          <BringForwardButton />
        </div>
      </ControlGroup>
      <ImgFilters img={img} />
      <BrightnessController img={img} />
      <ScaleController img={img} />
      <RotationController img={img} />
      <div className={cssMaskedPhoto.movementTools}>
        <MovementArrows img={img} />
        <MirrorArrows img={img} />
      </div>
      {img.inside.map((insideImage, index) =>
        insideImage ? (
          <div
            key={`movementInsideImage_${img.id}_${index}`}
            className={cssMaskedPhoto.movementTools}>
            <div
              className={[
                cssMaskedPhoto.controlGroup,
                cssMaskedPhoto.controlGroupHalf,
              ].join(' ')}>
              <span
                className={['groupTitle', cssMaskedPhoto.maskedGroupTitle].join(
                  ' ',
                )}>
                {t('{{i}}. kis kép mozgatása', {i: index + 1})}
              </span>
              <div
                className={[
                  cssMaskedPhoto.controlWrapper,
                  cssMaskedPhoto.movementArrows,
                ].join(' ')}>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <RightArrowIcon
                    title={t('Mozgatás balra')}
                    className={cssMaskedPhoto.icon}
                    style={{transform: 'rotate(180deg)'}}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    position: {
                                      x: item.position.x - 1,
                                      y: item.position.y,
                                    },
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <RightArrowIcon
                    title={t('Mozgatás jobbra')}
                    className={cssMaskedPhoto.icon}
                    style={{transform: 'rotate(0)'}}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    position: {
                                      x: item.position.x + 1,
                                      y: item.position.y,
                                    },
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <RightArrowIcon
                    title={t('Mozgatás fel')}
                    className={cssMaskedPhoto.icon}
                    style={{transform: 'rotate(-90deg)'}}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    position: {
                                      x: item.position.x,
                                      y: item.position.y - 1,
                                    },
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <RightArrowIcon
                    title={t('Mozgatás le')}
                    className={cssMaskedPhoto.icon}
                    style={{transform: 'rotate(90deg)'}}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    position: {
                                      x: item.position.x,
                                      y: item.position.y + 1,
                                    },
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              className={[
                cssMaskedPhoto.controlGroup,
                cssMaskedPhoto.controlGroupHalf,
              ].join(' ')}>
              <div
                className={['groupTitle', cssMaskedPhoto.maskedGroupTitle].join(
                  ' ',
                )}>
                {t('{{i}}. kis kép mérete', {i: index + 1})}
              </div>
              <div className={cssMaskedPhoto.controlWrapper}>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <FontAwesomeIcon
                    icon={faMinus}
                    size="2x"
                    title={t('Kicsinyítés')}
                    className={cssMaskedPhoto.icon}
                    style={{
                      padding: '0 22px',
                    }}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    scale: item.scale / 1.1,
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
                <div className={cssMaskedPhoto.iconWrapper}>
                  <FontAwesomeIcon
                    icon={faPlus}
                    size="2x"
                    title={t('Nagyítás')}
                    className={cssMaskedPhoto.icon}
                    style={{
                      padding: '0 22px',
                    }}
                    onClick={() => {
                      dispatch(
                        setAttribute({
                          id: img.id,
                          data: {
                            inside: img.inside.map((item, i) =>
                              i === index && item
                                ? {
                                    ...item,
                                    scale: item.scale * 1.1,
                                  }
                                : item,
                            ),
                          },
                        }),
                      )
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null,
      )}
    </div>
  )
}
